import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

declare global {
  interface Window {
    supabase: SupabaseClient | null;
  }
}

const STORAGE_KEY = 'vsl-supabase-auth';
const DB_SCHEMA = 'public';

// Single instance of Supabase client
let supabaseInstance: SupabaseClient | null = null;

// Initialize function that ensures only one client is ever created
export const initializeSupabase = async () => {
  // Return existing instance if available
  if (supabaseInstance) {
    return supabaseInstance;
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseKey) {
    throw new Error('Supabase configuration missing');
  }

  try {
    logger.info('Initializing Supabase client');
    
    // Only clear if no existing session
    if (typeof window !== 'undefined' && !localStorage.getItem(STORAGE_KEY)) {
      logger.info('No existing session found, clearing storage');
      localStorage.removeItem(STORAGE_KEY);
    } else {
      logger.info('Existing session found');
    }

    // Create new instance with consistent storage key
    supabaseInstance = createClient(supabaseUrl, supabaseKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        storageKey: STORAGE_KEY,
        flowType: 'pkce' // Use PKCE flow for better security
      },
      db: {
        schema: DB_SCHEMA
      }
    });

    // Attach to window for debugging and global access
    if (typeof window !== 'undefined') {
      window.supabase = supabaseInstance;
    }

    logger.info('Supabase client initialized successfully');
    return supabaseInstance;
  } catch (error) {
    logger.error('Failed to initialize Supabase', error);
    throw error;
  }
};

// Simple getter that returns the existing instance
export const getSupabase = () => {
  if (!supabaseInstance) {
    throw new Error('Supabase client not initialized');
  }
  return supabaseInstance;
};
